import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '~/libs/api.ts';
import type { RootState } from '~/store/store.ts';
import { thunkErrorHandler } from '~/store/utils.ts';
import type {
  TCreateGuaranteesApplicationRequest,
  TFetchGuaranteesAccountsRequest,
  TFetchGuaranteesApplicationRequest,
  TFetchGuaranteesApplicationTimelineRequest,
  TFetchGuaranteesApplicationsRequest,
  TFetchGuaranteesApprovalCommentRequest,
  TFetchGuaranteesBeneficiaryRequest,
  TFetchGuaranteesBaseRelationsRequest,
  TFetchGuaranteesFormRequest,
  TFetchGuaranteesMainDealConditionsRequest,
  TFetchGuaranteesExecutionFormRequest,
  TFetchGuaranteesManagerTasksRequest,
  TFetchGuaranteesManagerTermsRequest,
  TFetchGuaranteesMainDealsRequest,
  TFetchGuaranteesAmTemplateDocumentRequest,
  TFetchGuaranteesConclusionTemplateDocumentRequest,
  TGenerateGuaranteesConclusionAttachmentRequest,
  TGuaranteesAccountsResponse,
  TGuaranteesAmTemplateDocumentResponse,
  TGuaranteesApplicationTenderInfo,
  TGuaranteesApprovalCommentResponse,
  TGuaranteesConclusionTemplateDocumentResponse,
  TGenerateGuaranteesConclusionAttachmentResponse,
  TFetchGuaranteesSecurityCurrenciesRequest,
  TFetchGuaranteesApplicationTenderInfoRequest,
  TFetchGuaranteesProzorroTenderRequest,
  TGuaranteesApplication,
  TGuaranteesApplicationSelectionPatchResult,
  TGuaranteesApplicationsResponse,
  TGuaranteesBeneficiary,
  TGuaranteesBeneficiaryResponse,
  TGuaranteesBaseRelations,
  TGuaranteesForm,
  TGuaranteesMainDealsResponse,
  TGuaranteesPrincipal,
  TGuaranteesProzorroTender,
  TGuaranteesSecurityCurrenciesResponse,
  TGuaranteesSpringPageResponse,
  TGuaranteesTimelineEvent,
  TCheckGuaranteesApplicationRequest,
  TFetchGuaranteesAdministrationTasksRequest,
  TFetchGuaranteesTaskSupervisorsRequest,
  TPatchGuaranteesApplicationMainDealRequest,
  TPatchGuaranteesApplicationSecretRequest,
  TPatchGuaranteesApplicationTenderRequest,
  TPatchGuaranteesApplicationAssigneeRequest,
  TPatchGuaranteesApplicationAssigneeResponse,
  TPatchGuaranteesBaseRelationsRequest,
  TPatchGuaranteesBeneficiaryRequest,
  TPatchGuaranteesExecutionFormRequest,
  TPatchGuaranteesFormRequest,
  TPatchGuaranteesMainDealConditionsRequest,
  TPatchGuaranteesManagerTermsRequest,
  TPatchGuaranteesPrincipalRequest,
  TPatchGuaranteesRootApplicationSectionRequest,
  TPutGuaranteesSecurityCurrenciesRequest,
  TFetchGuaranteesPrincipalRequest,
  TGuaranteesAdministrationSupervisorInfoResponse,
  TGuaranteesManagerSupervisorInfoResponse,
  TGuaranteesUakdoSupervisorInfoResponse,
  TGuaranteesManagerTasksResponse,
  TGuaranteesManagerTerms,
  TGuaranteesMainDealConditions,
  TGuaranteesExecutionForm,
  TFetchGuaranteesUdbtfTemplateDocumentRequest,
  TGuaranteesAdministrationRolesResponse,
  TGuaranteesAdministrationTasksResponse,
  TGuaranteesTaskSupervisorsResponse,
  TGuaranteesUdbtfTemplateDocumentResponse,
  TDeleteGuaranteesClientDocumentRequest,
  TDownloadGuaranteesClientDocumentRequest,
  TDownloadGuaranteesClientDocumentResponse,
  TFetchGuaranteesApplicationFilesRequest,
  TFetchGuaranteesApplicationFilesResponse,
  TUploadGuaranteesClientDocumentRequest,
  TUploadGuaranteesClientDocumentResponse,
  TSubmitGuaranteesApplicationByClientRequest,
  TSubmitGuaranteesApplicationByClientResponse,
  TSubmitGuaranteesApprovalLetterByClientRequest,
  TSubmitGuaranteesApprovalLetterByClientResponse,
  TSubmitGuaranteesApplicationByAmManagerRequest,
  TSubmitGuaranteesApplicationByAmManagerResponse,
  TSubmitGuaranteesApprovalLetterByAmManagerRequest,
  TSubmitGuaranteesApprovalLetterByAmManagerResponse,
  TSubmitGuaranteesApplicationByManagerRequest,
  TSubmitGuaranteesApplicationByManagerResponse,
  TSubmitGuaranteesApplicationByUdbtfRequest,
  TSubmitGuaranteesApplicationByUdbtfResponse,
  TSubmitGuaranteesApprovalLetterByUdbtfRequest,
  TSubmitGuaranteesApprovalLetterByUdbtfResponse,
  TSubmitGuaranteesApprovalLetterByUsapokRequest,
  TSubmitGuaranteesApprovalLetterByUsapokResponse,
  TSubmitGuaranteesApplicationByUakdoRequest,
  TSubmitGuaranteesApplicationByUakdoResponse,
  TSubmitGuaranteesApplicationByUakdoControllerRequest,
  TSubmitGuaranteesApplicationByUakdoControllerResponse,
  TRejectGuaranteesApplicationByUakdoControllerRequest,
  TRejectGuaranteesApplicationByUakdoControllerResponse,
  TPatchGuaranteesIssueGuaranteeRequest,
  TPatchGuaranteesIssueGuaranteeResponse,
  TUpdateGuaranteesApplicationStatusRequest,
  TUpdateGuaranteesApplicationStatusResponse,
  TUpdateGuaranteesUmvnApprovalStatusRequest,
  TUpdateGuaranteesUmvnApprovalStatusResponse,
  TUpdateGuaranteesUoApprovalStatusRequest,
  TUpdateGuaranteesUoApprovalStatusResponse,
  TUpdateGuaranteesUdbtfApprovalStatusRequest,
  TUpdateGuaranteesUdbtfApprovalStatusResponse,
} from '../types.ts';
import type { TSupervisorAdministrationPageKey } from '~/modules/supervisor-administration/types.ts';
import { getSupervisorAdministrationPageConfig } from '~/modules/supervisor-administration/config.ts';
import { GuaranteesFetchStatus } from '../types.ts';
import {
  getGuaranteesAmTemplateDocumentFileName,
  buildGuaranteesApplicationTenderLotsPayload,
  getGuaranteesConclusionTemplateDocumentFileName,
  getGuaranteesUdbtfTemplateDocumentFileName,
  buildGuaranteesAdministrationTasksParams,
  buildGuaranteesMainDealsRequestKey,
  buildGuaranteesManagerTasksParams,
  isGuaranteesApplicationPayload,
  isMissingGuaranteesBaseRelationsError,
  normalizeGuaranteesApplicationTimelineResponse,
  normalizeGuaranteesApplicationsResponse,
  normalizeGuaranteesAdministrationTasksResponse,
  normalizeGuaranteesBeneficiaryResponse,
  normalizeGuaranteesManagerTasksResponse,
  preserveGuaranteesBeneficiaryCountryNames,
} from '../utils.ts';

export const fetchGuaranteesApplications = createAsyncThunk<
  TGuaranteesApplicationsResponse,
  TFetchGuaranteesApplicationsRequest
>('guaranteesPage/fetchApplications', async ({ contragentId, page, size }, { rejectWithValue }) => {
  try {
    const { data } = await api.get<TGuaranteesSpringPageResponse<TGuaranteesApplication>>(
      `/guarantee-api/contragents/${contragentId}/applications`,
      {
        params: { page, size },
      }
    );

    return normalizeGuaranteesApplicationsResponse(data, page, size);
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const fetchGuaranteesManagerTasks = createAsyncThunk<
  TGuaranteesManagerTasksResponse,
  TFetchGuaranteesManagerTasksRequest
>(
  'guaranteesPage/fetchManagerTasks',
  async (request, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesSpringPageResponse<TGuaranteesApplication>>(
        '/guarantee-api/managers/tasks',
        {
          params: buildGuaranteesManagerTasksParams(request),
        }
      );

      return normalizeGuaranteesManagerTasksResponse(data, request.page, request.size);
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesAdministrationRoles = createAsyncThunk<
  TGuaranteesAdministrationRolesResponse,
  void,
  { state: RootState }
>(
  'guaranteesPage/fetchAdministrationRoles',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesAdministrationRolesResponse>(
        '/guarantee-api/administrations/roles'
      );

      return {
        roles: Array.isArray(data?.roles) ? data.roles : [],
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  },
  {
    condition: (_, { getState }) =>
      getState().guarantees.administrationRoles.status === GuaranteesFetchStatus.Idle,
  }
);

export const fetchGuaranteesAdministrationTasks = createAsyncThunk<
  TGuaranteesAdministrationTasksResponse,
  TFetchGuaranteesAdministrationTasksRequest
>(
  'guaranteesPage/fetchAdministrationTasks',
  async (request, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesSpringPageResponse<TGuaranteesApplication>>(
        '/guarantee-api/administrations/tasks',
        {
          params: buildGuaranteesAdministrationTasksParams(request),
        }
      );

      return normalizeGuaranteesAdministrationTasksResponse(data, request.page, request.size);
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const updateGuaranteesApplicationStatus = createAsyncThunk<
  TUpdateGuaranteesApplicationStatusResponse,
  TUpdateGuaranteesApplicationStatusRequest
>(
  'guaranteesPage/updateApplicationStatus',
  async ({ applicationId, applicationStatus, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.post<TUpdateGuaranteesApplicationStatusResponse>(
        `/guarantee-api/contragents/applications/${applicationId}/statuses`,
        null,
        {
          params: {
            applicationStatus,
            version,
          },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesTaskSupervisors = createAsyncThunk<
  TGuaranteesTaskSupervisorsResponse,
  TFetchGuaranteesTaskSupervisorsRequest
>(
  'guaranteesPage/fetchTaskSupervisors',
  async ({ taskId, unit }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesTaskSupervisorsResponse>(
        `/guarantee-api/administrations/tasks/${encodeURIComponent(taskId)}/suppervisors`,
        {
          params: { unit },
        }
      );

      return {
        unit: data?.unit ?? unit,
        assignedEmployeeEmail: data?.assignedEmployeeEmail ?? null,
        supervisorEmails: Array.isArray(data?.supervisorEmails)
          ? data.supervisorEmails
          : [],
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const patchGuaranteesApplicationAssignee = createAsyncThunk<
  TPatchGuaranteesApplicationAssigneeResponse,
  TPatchGuaranteesApplicationAssigneeRequest
>(
  'guaranteesPage/patchApplicationAssignee',
  async ({ applicationId, payload, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch<TPatchGuaranteesApplicationAssigneeResponse>(
        `/guarantee-api/contragents/applications/${encodeURIComponent(applicationId)}/assignee`,
        payload,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const updateGuaranteesUdbtfApprovalStatus = createAsyncThunk<
  TUpdateGuaranteesUdbtfApprovalStatusResponse,
  TUpdateGuaranteesUdbtfApprovalStatusRequest
>(
  'guaranteesPage/updateUdbtfApprovalStatus',
  async (
    { applicationApprovalStatus, applicationId, description, version },
    { rejectWithValue }
  ) => {
    try {
      const params: Record<string, string | number> = {
        applicationApprovalStatus,
        version,
      };
      const normalizedDescription = description?.trim();

      if (normalizedDescription) {
        params.description = normalizedDescription;
      }

      const { data } = await api.post<TUpdateGuaranteesUdbtfApprovalStatusResponse>(
        `/guarantee-api/contragents/applications/${encodeURIComponent(applicationId)}/udbtf/approval-statuses`,
        null,
        {
          params,
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const updateGuaranteesUoApprovalStatus = createAsyncThunk<
  TUpdateGuaranteesUoApprovalStatusResponse,
  TUpdateGuaranteesUoApprovalStatusRequest
>(
  'guaranteesPage/updateUoApprovalStatus',
  async (
    { applicationApprovalStatus, applicationId, description, version },
    { rejectWithValue }
  ) => {
    try {
      const params: Record<string, string | number> = {
        applicationApprovalStatus,
        version,
      };
      const normalizedDescription = description?.trim();

      if (normalizedDescription) {
        params.description = normalizedDescription;
      }

      const { data } = await api.post<TUpdateGuaranteesUoApprovalStatusResponse>(
        `/guarantee-api/contragents/applications/${encodeURIComponent(applicationId)}/uo/approval-statuses`,
        null,
        {
          params,
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const updateGuaranteesUmvnApprovalStatus = createAsyncThunk<
  TUpdateGuaranteesUmvnApprovalStatusResponse,
  TUpdateGuaranteesUmvnApprovalStatusRequest
>(
  'guaranteesPage/updateUmvnApprovalStatus',
  async (
    { applicationApprovalStatus, applicationId, description, version },
    { rejectWithValue }
  ) => {
    try {
      const params: Record<string, string | number> = {
        applicationApprovalStatus,
        version,
      };
      const normalizedDescription = description?.trim();

      if (normalizedDescription) {
        params.description = normalizedDescription;
      }

      const { data } = await api.post<TUpdateGuaranteesUmvnApprovalStatusResponse>(
        `/guarantee-api/contragents/applications/${encodeURIComponent(applicationId)}/umvn/approval-statuses`,
        null,
        {
          params,
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const submitGuaranteesApplicationByManager = createAsyncThunk<
  TSubmitGuaranteesApplicationByManagerResponse,
  TSubmitGuaranteesApplicationByManagerRequest
>(
  'guaranteesPage/submitApplicationByManager',
  async ({ applicationId, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.post<TSubmitGuaranteesApplicationByManagerResponse>(
        `/guarantee-api/contragents/applications/${applicationId}/sabmit-by-manager`,
        null,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const submitGuaranteesApplicationByClient = createAsyncThunk<
  TSubmitGuaranteesApplicationByClientResponse,
  TSubmitGuaranteesApplicationByClientRequest
>(
  'guaranteesPage/submitApplicationByClient',
  async ({ applicationId, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.post<TSubmitGuaranteesApplicationByClientResponse>(
        `/guarantee-api/contragents/applications/${applicationId}/sabmit-by-client`,
        null,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const submitGuaranteesApprovalLetterByClient = createAsyncThunk<
  TSubmitGuaranteesApprovalLetterByClientResponse,
  TSubmitGuaranteesApprovalLetterByClientRequest
>(
  'guaranteesPage/submitApprovalLetterByClient',
  async ({ applicationId, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.post<TSubmitGuaranteesApprovalLetterByClientResponse>(
        `/guarantee-api/contragents/applications/${applicationId}/approval-letters/sabmit-by-client`,
        null,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const submitGuaranteesApplicationByUdbtf = createAsyncThunk<
  TSubmitGuaranteesApplicationByUdbtfResponse,
  TSubmitGuaranteesApplicationByUdbtfRequest
>(
  'guaranteesPage/submitApplicationByUdbtf',
  async ({ applicationId, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.post<TSubmitGuaranteesApplicationByUdbtfResponse>(
        `/guarantee-api/contragents/applications/${applicationId}/sabmit-by-udbtf`,
        null,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const submitGuaranteesApprovalLetterByUdbtf = createAsyncThunk<
  TSubmitGuaranteesApprovalLetterByUdbtfResponse,
  TSubmitGuaranteesApprovalLetterByUdbtfRequest
>(
  'guaranteesPage/submitApprovalLetterByUdbtf',
  async ({ applicationId, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.post<TSubmitGuaranteesApprovalLetterByUdbtfResponse>(
        `/guarantee-api/contragents/applications/${applicationId}/approval-letters/sabmit-by-udbtf`,
        null,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const submitGuaranteesApprovalLetterByUsapok = createAsyncThunk<
  TSubmitGuaranteesApprovalLetterByUsapokResponse,
  TSubmitGuaranteesApprovalLetterByUsapokRequest
>(
  'guaranteesPage/submitApprovalLetterByUsapok',
  async ({ applicationId, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.post<TSubmitGuaranteesApprovalLetterByUsapokResponse>(
        `/guarantee-api/contragents/applications/${applicationId}/approval-letters/sabmit-by-usapok`,
        null,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const submitGuaranteesApplicationByUakdo = createAsyncThunk<
  TSubmitGuaranteesApplicationByUakdoResponse,
  TSubmitGuaranteesApplicationByUakdoRequest
>(
  'guaranteesPage/submitApplicationByUakdo',
  async ({ applicationId, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.post<TSubmitGuaranteesApplicationByUakdoResponse>(
        `/guarantee-api/contragents/applications/${applicationId}/sabmit-by-uakdo`,
        null,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const submitGuaranteesApplicationByUakdoController = createAsyncThunk<
  TSubmitGuaranteesApplicationByUakdoControllerResponse,
  TSubmitGuaranteesApplicationByUakdoControllerRequest
>(
  'guaranteesPage/submitApplicationByUakdoController',
  async ({ applicationId, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.post<TSubmitGuaranteesApplicationByUakdoControllerResponse>(
        `/guarantee-api/contragents/applications/${applicationId}/sabmit-by-uakdo-controller`,
        null,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const rejectGuaranteesApplicationByUakdoController = createAsyncThunk<
  TRejectGuaranteesApplicationByUakdoControllerResponse,
  TRejectGuaranteesApplicationByUakdoControllerRequest
>(
  'guaranteesPage/rejectApplicationByUakdoController',
  async ({ applicationId, payload, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.post<TRejectGuaranteesApplicationByUakdoControllerResponse>(
        `/guarantee-api/contragents/applications/${applicationId}/reject-by-uakdo-controller`,
        payload,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const submitGuaranteesApplicationByAmManager = createAsyncThunk<
  TSubmitGuaranteesApplicationByAmManagerResponse,
  TSubmitGuaranteesApplicationByAmManagerRequest
>(
  'guaranteesPage/submitApplicationByAmManager',
  async ({ applicationId, coverageType, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.post<TSubmitGuaranteesApplicationByAmManagerResponse>(
        `/guarantee-api/contragents/applications/${applicationId}/sabmit-by-am-managers`,
        null,
        {
          params: { coverageType, version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const submitGuaranteesApprovalLetterByAmManager = createAsyncThunk<
  TSubmitGuaranteesApprovalLetterByAmManagerResponse,
  TSubmitGuaranteesApprovalLetterByAmManagerRequest
>(
  'guaranteesPage/submitApprovalLetterByAmManager',
  async ({ applicationId, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.post<TSubmitGuaranteesApprovalLetterByAmManagerResponse>(
        `/guarantee-api/contragents/applications/${applicationId}/approval-letters/sabmit-by-am-manager`,
        null,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesManagerSupervisorInfo = createAsyncThunk<
  TGuaranteesManagerSupervisorInfoResponse,
  void,
  { state: RootState }
>(
  'guaranteesPage/fetchManagerSupervisorInfo',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesManagerSupervisorInfoResponse>(
        '/guarantee-api/managers/supervisor-info'
      );

      return {
        managerId: String(data?.managerId ?? '').trim(),
        hasPmAccess: !!data?.hasPmAccess,
        hasAmAccess: !!data?.hasAmAccess,
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  },
  {
    condition: (_, { getState }) =>
      getState().guarantees.managerSupervisorInfo.status === GuaranteesFetchStatus.Idle,
  }
);

export const fetchGuaranteesUakdoSupervisorInfo = createAsyncThunk<
  TGuaranteesUakdoSupervisorInfoResponse,
  void,
  { state: RootState }
>(
  'guaranteesPage/fetchUakdoSupervisorInfo',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesUakdoSupervisorInfoResponse>(
        '/guarantee-api/managers/uakdo/supervisor-info'
      );

      return {
        hasResponsibleAccess: !!data?.hasResponsibleAccess,
        hasControllerAccess: !!data?.hasControllerAccess,
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  },
  {
    condition: (_, { getState }) =>
      getState().guarantees.uakdoSupervisorInfo.status === GuaranteesFetchStatus.Idle,
  }
);

export const fetchGuaranteesAdministrationSupervisorInfo = createAsyncThunk<
  TGuaranteesAdministrationSupervisorInfoResponse,
  { pageKey: TSupervisorAdministrationPageKey },
  { state: RootState }
>(
  'guaranteesPage/fetchAdministrationSupervisorInfo',
  async ({ pageKey }, { rejectWithValue }) => {
    try {
      const apiPathSegment = getSupervisorAdministrationPageConfig(pageKey).apiPathSegment;
      const { data } = await api.get<
        TGuaranteesAdministrationSupervisorInfoResponse & {
          hasAdministrationAccess?: boolean;
        }
      >(
        `/guarantee-api/managers/${apiPathSegment}/supervisor-info`
      );

      return {
        hasAccess: !!(data?.hasAccess ?? data?.hasAdministrationAccess),
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  },
  {
    condition: ({ pageKey }, { getState }) =>
      getState().guarantees.administrationSupervisorInfoByPage[pageKey].status ===
      GuaranteesFetchStatus.Idle,
  }
);

export const fetchGuaranteesApplicationTimeline = createAsyncThunk<
  TGuaranteesTimelineEvent[],
  TFetchGuaranteesApplicationTimelineRequest
>(
  'guaranteesPage/fetchApplicationTimeline',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<unknown>(
        `/guarantee-api/applications/${applicationId}/application-histories`
      );

      return normalizeGuaranteesApplicationTimelineResponse(data);
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesApplication = createAsyncThunk<
  TGuaranteesApplication,
  TFetchGuaranteesApplicationRequest
>(
  'guaranteesPage/fetchApplication',
  async ({ contragentId, applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesApplication>(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}`
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesUdbtfTemplateDocument = createAsyncThunk<
  TGuaranteesUdbtfTemplateDocumentResponse,
  TFetchGuaranteesUdbtfTemplateDocumentRequest
>(
  'guaranteesPage/fetchUdbtfTemplateDocument',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const response = await api.get<Blob>(
        `/guarantee-api/applications/${encodeURIComponent(applicationId)}/udbtf/templates`,
        {
          headers: {
            Accept: '*/*',
          },
          params: {
            isAttached: true,
          },
          responseType: 'blob',
        }
      );
      const responseContentType = response.headers['content-type'];
      const contentType =
        typeof responseContentType === 'string' && responseContentType.trim()
          ? responseContentType
          : undefined;

      return {
        data: response.data,
        filename: getGuaranteesUdbtfTemplateDocumentFileName(
          response.headers['content-disposition'],
          applicationId
        ),
        contentType,
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesAmTemplateDocument = createAsyncThunk<
  TGuaranteesAmTemplateDocumentResponse,
  TFetchGuaranteesAmTemplateDocumentRequest
>(
  'guaranteesPage/fetchAmTemplateDocument',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const response = await api.get<Blob>(
        `/guarantee-api/applications/${encodeURIComponent(applicationId)}/managers/templates`,
        {
          headers: {
            Accept: '*/*',
          },
          params: {
            isAttached: true,
          },
          responseType: 'blob',
        }
      );
      const responseContentType = response.headers['content-type'];
      const contentType =
        typeof responseContentType === 'string' && responseContentType.trim()
          ? responseContentType
          : undefined;

      return {
        data: response.data,
        filename: getGuaranteesAmTemplateDocumentFileName(
          response.headers['content-disposition'],
          applicationId
        ),
        contentType,
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesConclusionTemplateDocument = createAsyncThunk<
  TGuaranteesConclusionTemplateDocumentResponse,
  TFetchGuaranteesConclusionTemplateDocumentRequest
>(
  'guaranteesPage/fetchConclusionTemplateDocument',
  async ({ applicationId, approvalType }, { rejectWithValue }) => {
    try {
      const response = await api.get<Blob>(
        `/guarantee-api/applications/${encodeURIComponent(applicationId)}/${approvalType}/conclusion-templates`,
        {
          headers: {
            Accept: '*/*',
          },
          params: {
            isAttached: true,
          },
          responseType: 'blob',
        }
      );
      const responseContentType = response.headers['content-type'];
      const contentType =
        typeof responseContentType === 'string' && responseContentType.trim()
          ? responseContentType
          : undefined;

      return {
        data: response.data,
        filename: getGuaranteesConclusionTemplateDocumentFileName(
          response.headers['content-disposition'],
          applicationId,
          approvalType
        ),
        contentType,
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesApprovalComment = createAsyncThunk<
  TGuaranteesApprovalCommentResponse,
  TFetchGuaranteesApprovalCommentRequest
>(
  'guaranteesPage/fetchApprovalComment',
  async ({ applicationId, approvalType }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesApprovalCommentResponse>(
        `/guarantee-api/contragents/applications/${encodeURIComponent(applicationId)}/${approvalType}/approval-comments`
      );

      return {
        ...(data ?? {}),
        applicationId,
        approvalType,
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const generateGuaranteesConclusionAttachment = createAsyncThunk<
  TGenerateGuaranteesConclusionAttachmentResponse,
  TGenerateGuaranteesConclusionAttachmentRequest
>(
  'guaranteesPage/generateConclusionAttachment',
  async ({ applicationId, approvalType, comment, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.post<TGenerateGuaranteesConclusionAttachmentResponse>(
        `/guarantee-api/applications/${encodeURIComponent(applicationId)}/${approvalType}/conclusion-templates`,
        {
          comment,
        },
        {
          params: {
            version,
          },
        }
      );

      return data ?? null;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesApplicationFiles = createAsyncThunk<
  TFetchGuaranteesApplicationFilesResponse,
  TFetchGuaranteesApplicationFilesRequest
>(
  'guaranteesPage/fetchApplicationFiles',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TFetchGuaranteesApplicationFilesResponse>(
        `/guarantee-api/applications/${encodeURIComponent(applicationId)}/application-files`
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const uploadGuaranteesClientDocument = createAsyncThunk<
  TUploadGuaranteesClientDocumentResponse,
  TUploadGuaranteesClientDocumentRequest
>(
  'guaranteesPage/uploadClientDocument',
  async ({ applicationId, fileType, multipartFile }, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      formData.append('multipartFile', multipartFile);

      const { data } = await api.post<TUploadGuaranteesClientDocumentResponse>(
        `/guarantee-api/files/applications/${encodeURIComponent(applicationId)}/upload`,
        formData,
        {
          params: {
            fileType,
          },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const deleteGuaranteesClientDocument = createAsyncThunk<
  void,
  TDeleteGuaranteesClientDocumentRequest
>(
  'guaranteesPage/deleteClientDocument',
  async ({ applicationFileId }, { rejectWithValue }) => {
    try {
      await api.delete(
        `/guarantee-api/applications/application-files/${encodeURIComponent(applicationFileId)}`
      );
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const downloadGuaranteesClientDocument = createAsyncThunk<
  TDownloadGuaranteesClientDocumentResponse,
  TDownloadGuaranteesClientDocumentRequest
>(
  'guaranteesPage/downloadClientDocument',
  async ({ id, isAttached }, { rejectWithValue }) => {
    try {
      const response = await api.get<Blob>(
        `/guarantee-api/files/download/${encodeURIComponent(id)}`,
        {
          headers: {
            Accept: '*/*',
          },
          params: {
            isAttached,
          },
          responseType: 'blob',
        }
      );
      const responseContentType = response.headers['content-type'];
      const contentType =
        typeof responseContentType === 'string' && responseContentType.trim()
          ? responseContentType
          : undefined;
      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `document_${id}`;

      return {
        data: response.data,
        filename,
        contentType,
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const patchGuaranteesRootApplicationSection = createAsyncThunk<
  TGuaranteesApplication,
  TPatchGuaranteesRootApplicationSectionRequest
>(
  'guaranteesPage/patchRootApplicationSection',
  async ({ contragentId, applicationId, payload, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch<TGuaranteesApplication>(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}`,
        payload,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesPrincipal = createAsyncThunk<
  TGuaranteesPrincipal,
  TFetchGuaranteesPrincipalRequest
>('guaranteesPage/fetchPrincipal', async ({ contragentId, applicationId }, { rejectWithValue }) => {
  try {
    const { data } = await api.get<TGuaranteesPrincipal>(
      `/guarantee-api/contragents/${contragentId}/applications/${applicationId}/principal`
    );

    return data;
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const patchGuaranteesPrincipal = createAsyncThunk<
  TGuaranteesPrincipal | TGuaranteesApplication,
  TPatchGuaranteesPrincipalRequest
>(
  'guaranteesPage/patchPrincipal',
  async ({ contragentId, applicationId, payload, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch<TGuaranteesPrincipal | TGuaranteesApplication>(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}/principal`,
        payload,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesBeneficiary = createAsyncThunk<
  TGuaranteesBeneficiaryResponse,
  TFetchGuaranteesBeneficiaryRequest
>(
  'guaranteesPage/fetchBeneficiary',
  async ({ contragentId, applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesBeneficiary | TGuaranteesBeneficiaryResponse>(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}/beneficiary`
      );

      return Array.isArray(data) ? data : [data];
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const patchGuaranteesBeneficiary = createAsyncThunk<
  TGuaranteesBeneficiaryResponse | TGuaranteesApplication,
  TPatchGuaranteesBeneficiaryRequest
>(
  'guaranteesPage/patchBeneficiary',
  async ({ contragentId, applicationId, payload, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch<
        TGuaranteesApplication | TGuaranteesBeneficiary | TGuaranteesBeneficiaryResponse
      >(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}/beneficiary`,
        payload,
        {
          params: { version },
        }
      );

      if (isGuaranteesApplicationPayload(data)) {
        return data;
      }

      return preserveGuaranteesBeneficiaryCountryNames(
        normalizeGuaranteesBeneficiaryResponse(data),
        payload
      );
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesBaseRelations = createAsyncThunk<
  TGuaranteesBaseRelations | null,
  TFetchGuaranteesBaseRelationsRequest
>(
  'guaranteesPage/fetchBaseRelations',
  async ({ contragentId, applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesBaseRelations>(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}/base-relations`
      );

      return data;
    } catch (error) {
      if (isMissingGuaranteesBaseRelationsError(error)) {
        return null;
      }

      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const patchGuaranteesBaseRelations = createAsyncThunk<
  TGuaranteesBaseRelations | TGuaranteesApplication,
  TPatchGuaranteesBaseRelationsRequest
>(
  'guaranteesPage/patchBaseRelations',
  async ({ contragentId, applicationId, payload, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch<TGuaranteesBaseRelations | TGuaranteesApplication>(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}/base-relations`,
        payload,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesManagerTerms = createAsyncThunk<
  TGuaranteesManagerTerms,
  TFetchGuaranteesManagerTermsRequest
>(
  'guaranteesPage/fetchManagerTerms',
  async ({ contragentId, applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesManagerTerms>(
        `/guarantee-api/contragents/contragents/${contragentId}/applications/${applicationId}/manager-terms`
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const patchGuaranteesManagerTerms = createAsyncThunk<
  TGuaranteesManagerTerms | TGuaranteesApplication,
  TPatchGuaranteesManagerTermsRequest
>(
  'guaranteesPage/patchManagerTerms',
  async ({ contragentId, applicationId, payload, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch<TGuaranteesManagerTerms | TGuaranteesApplication>(
        `/guarantee-api/contragents/contragents/${contragentId}/applications/${applicationId}/manager-terms`,
        payload,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const patchGuaranteesExecutionForm = createAsyncThunk<
  TGuaranteesExecutionForm | TGuaranteesApplication,
  TPatchGuaranteesExecutionFormRequest
>(
  'guaranteesPage/patchExecutionForm',
  async ({ applicationId, payload, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch<TGuaranteesExecutionForm | TGuaranteesApplication>(
        `/guarantee-api/applications/${applicationId}/execution-form`,
        payload,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const patchGuaranteesIssueGuarantee = createAsyncThunk<
  TPatchGuaranteesIssueGuaranteeResponse,
  TPatchGuaranteesIssueGuaranteeRequest
>(
  'guaranteesPage/patchIssueGuarantee',
  async ({ applicationId, payload, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch<TPatchGuaranteesIssueGuaranteeResponse>(
        `/guarantee-api/applications/${applicationId}/issue-guarantee`,
        payload,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesExecutionForm = createAsyncThunk<
  TGuaranteesExecutionForm,
  TFetchGuaranteesExecutionFormRequest
>(
  'guaranteesPage/fetchExecutionForm',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesExecutionForm>(
        `/guarantee-api/contragents/applications/${applicationId}/execution-form`
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesForm = createAsyncThunk<TGuaranteesForm, TFetchGuaranteesFormRequest>(
  'guaranteesPage/fetchGuaranteeForm',
  async ({ contragentId, applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesForm>(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}/guarantee-form`
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const patchGuaranteesForm = createAsyncThunk<
  TGuaranteesForm | TGuaranteesApplication,
  TPatchGuaranteesFormRequest
>(
  'guaranteesPage/patchGuaranteeForm',
  async ({ contragentId, applicationId, payload, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch<TGuaranteesForm | TGuaranteesApplication>(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}/guarantee-form`,
        payload,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesMainDealConditions = createAsyncThunk<
  TGuaranteesMainDealConditions,
  TFetchGuaranteesMainDealConditionsRequest
>(
  'guaranteesPage/fetchMainDealConditions',
  async ({ contragentId, applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesMainDealConditions>(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}/main-deal-conditions`
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const patchGuaranteesMainDealConditions = createAsyncThunk<
  TGuaranteesMainDealConditions,
  TPatchGuaranteesMainDealConditionsRequest
>(
  'guaranteesPage/patchMainDealConditions',
  async ({ contragentId, applicationId, payload, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch<TGuaranteesMainDealConditions>(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}/main-deal-conditions`,
        payload,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const patchGuaranteesApplicationTender = createAsyncThunk<
  TGuaranteesApplicationSelectionPatchResult,
  TPatchGuaranteesApplicationTenderRequest
>(
  'guaranteesPage/patchApplicationTender',
  async (
    { contragentId, applicationId, tenderPublicId, version, selectedLotIds },
    { rejectWithValue }
  ) => {
    try {
      const normalizedTenderPublicId = tenderPublicId.trim();

      if (!normalizedTenderPublicId) {
        return rejectWithValue({
          message: 'Не отримано внутрішній id тендера з Prozorro',
        });
      }

      const payload = buildGuaranteesApplicationTenderLotsPayload(selectedLotIds);

      const { data, status } = await api.patch<TGuaranteesApplication>(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}/tender`,
        payload,
        {
          params: {
            tenderPublicId: normalizedTenderPublicId,
            version,
          },
        }
      );

      return {
        application: data ?? null,
        status,
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesApplicationTenderInfo = createAsyncThunk<
  TGuaranteesApplicationTenderInfo,
  TFetchGuaranteesApplicationTenderInfoRequest
>(
  'guaranteesPage/fetchApplicationTenderInfo',
  async ({ contragentId, applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesApplicationTenderInfo>(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}/tender`
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesProzorroTenderById = createAsyncThunk<
  TGuaranteesProzorroTender,
  TFetchGuaranteesProzorroTenderRequest
>('guaranteesPage/fetchProzorroTenderById', async ({ tenderPublicId }, { rejectWithValue }) => {
  try {
    const normalizedTenderPublicId = tenderPublicId.trim().replace(/^ua(?=-)/i, 'UA');
    const { data } = await api.get<TGuaranteesProzorroTender>(
      `/dictionary-service/prozorro/${encodeURIComponent(normalizedTenderPublicId)}`
    );

    return data;
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const fetchGuaranteesMainDeals = createAsyncThunk<
  TGuaranteesMainDealsResponse,
  TFetchGuaranteesMainDealsRequest,
  { state: RootState }
>(
  'guaranteesPage/fetchMainDeals',
  async ({ contragentId, page = 0, size = 20, sort }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesMainDealsResponse>(
        `/dictionary-service/dictionary/contragents/${contragentId}/main-deals`,
        {
          params: {
            page,
            size,
            ...(sort?.length ? { sort } : {}),
          },
        }
      );

      return {
        ...data,
        content: Array.isArray(data.content) ? data.content : [],
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  },
  {
    condition: (request, { getState }) => {
      const state = getState().guarantees;
      const requestKey = buildGuaranteesMainDealsRequestKey(request);
      const isSameRequest = state.mainDealsRequestKey === requestKey;
      const isRequestAlreadyHandled =
        state.mainDeals.status === GuaranteesFetchStatus.Loading ||
        state.mainDeals.status === GuaranteesFetchStatus.Success;

      return !(isSameRequest && isRequestAlreadyHandled);
    },
  }
);

export const patchGuaranteesApplicationMainDeal = createAsyncThunk<
  TGuaranteesApplicationSelectionPatchResult,
  TPatchGuaranteesApplicationMainDealRequest
>(
  'guaranteesPage/patchApplicationMainDeal',
  async ({ contragentId, applicationId, mainDealId, version }, { rejectWithValue }) => {
    try {
      const normalizedMainDealId = mainDealId.trim();

      const { data, status } = await api.patch<TGuaranteesApplication>(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}/main-deal`,
        null,
        {
          params: {
            mainDealId: normalizedMainDealId,
            version,
          },
        }
      );

      return {
        application: data ?? null,
        status,
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesAccounts = createAsyncThunk<
  TGuaranteesAccountsResponse,
  TFetchGuaranteesAccountsRequest
>(
  'guaranteesPage/fetchAccounts',
  async ({ contragentId, isPrincipalDebitAccount }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesAccountsResponse>(
        `/dictionary-service/dictionary/contragents/${contragentId}/accounts`,
        {
          params: {
            isPrincipalDebitAccount,
          },
        }
      );

      return {
        ...data,
        content: Array.isArray(data.content) ? data.content : [],
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesPrincipalDebitAccounts = createAsyncThunk<
  TGuaranteesAccountsResponse,
  Pick<TFetchGuaranteesAccountsRequest, 'contragentId'>
>(
  'guaranteesPage/fetchPrincipalDebitAccounts',
  async ({ contragentId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesAccountsResponse>(
        `/dictionary-service/dictionary/contragents/${contragentId}/accounts`,
        {
          params: {
            isPrincipalDebitAccount: true,
          },
        }
      );

      return {
        ...data,
        content: Array.isArray(data.content) ? data.content : [],
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesSecurityCurrencies = createAsyncThunk<
  TGuaranteesSecurityCurrenciesResponse,
  TFetchGuaranteesSecurityCurrenciesRequest
>(
  'guaranteesPage/fetchSecurityCurrencies',
  async ({ contragentId, applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesSecurityCurrenciesResponse>(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}/security-currencies`
      );

      return {
        currencies: Array.isArray(data.currencies) ? data.currencies : [],
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const putGuaranteesSecurityCurrencies = createAsyncThunk<
  TGuaranteesApplication,
  TPutGuaranteesSecurityCurrenciesRequest
>(
  'guaranteesPage/putSecurityCurrencies',
  async ({ contragentId, applicationId, payload, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.put<TGuaranteesApplication>(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}/security-currencies`,
        payload,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const patchGuaranteesApplicationSecret = createAsyncThunk<
  TGuaranteesApplication,
  TPatchGuaranteesApplicationSecretRequest
>(
  'guaranteesPage/patchApplicationSecret',
  async ({ contragentId, applicationId, payload, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch<TGuaranteesApplication>(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}/secret`,
        payload,
        {
          params: { version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const checkGuaranteesApplicationRequest = createAsyncThunk<
  TGuaranteesApplication,
  TCheckGuaranteesApplicationRequest
>(
  'guaranteesPage/checkApplicationRequest',
  async (
    { contragentId, applicationId, checkAccountRestriction, version },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await api.post<TGuaranteesApplication>(
        `/guarantee-api/contragents/${contragentId}/applications/${applicationId}/request-check`,
        null,
        {
          params: { checkAccountRestriction, version },
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const createGuaranteesApplication = createAsyncThunk<
  TGuaranteesApplication,
  TCreateGuaranteesApplicationRequest
>('guaranteesPage/createApplication', async ({ contragentId, flowType }, { rejectWithValue }) => {
  try {
    const { data } = await api.post(
      `/guarantee-api/contragents/${contragentId}/applications`,
      null,
      {
        params: { flowType },
      }
    );

    return data;
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});
